import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, Calendar, CalendarDays, Clock, MapPin, Phone, Sandwich, Music, Brain, Dices, Mic, PartyPopper, UtensilsCrossed, Ticket, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import './Hero.css';
import { trackEvent } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EVENTBRITE_URL = 'https://www.eventbrite.com/o/royal-bar-grill-120928056108';
const ORDER_URL = 'https://royalbargrill.com';

const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const WEEK_EVENTS = {
  Sunday: { name: '$15 Indian Entree Special', Icon: UtensilsCrossed },
  Monday: { name: '$10 Cheeseburger Meal', Icon: Sandwich },
  Tuesday: { name: 'Music Bingo', Icon: Music },
  Wednesday: { name: 'Royal Mix-Up Trivia • 6:00 PM', Icon: Brain },
  Thursday: { name: 'Xtreme Bar Bingo', Icon: Dices },
  Friday: { name: 'Karaoke • 9:00 PM', Icon: Mic },
  // Saturday is resolved dynamically based on which Saturday of the month it is.
};

// Resolve a given day's event. Saturday varies by week-of-month:
//   2nd / 4th Saturday -> Line Dancing • 8:00 PM
//   3rd Saturday       -> Comedy Night
//   1st / 5th Saturday -> no event (returns null)
const getEventForDate = (date) => {
  const dayName = DAY_ORDER[date.getDay()];
  if (dayName === 'Saturday') {
    const nthSaturday = Math.ceil(date.getDate() / 7);
    if (nthSaturday === 3) return { name: 'Comedy Night', Icon: Mic };
    if (nthSaturday === 2 || nthSaturday === 4) return { name: 'Line Dancing • 8:00 PM — Returns Soon (Temporarily Paused)', Icon: PartyPopper };
    return null;
  }
  return WEEK_EVENTS[dayName];
};

// Today + the next days (Pacific Time). Days with no event (1st/5th Saturday)
// are skipped and the following day is pulled in, so 3 events always show.
const getWeekHighlights = () => {
  const base = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const highlights = [];
  for (let offset = 0; offset < 10 && highlights.length < 3; offset += 1) {
    const date = new Date(base);
    date.setDate(date.getDate() + offset);
    const event = getEventForDate(date);
    if (event) {
      highlights.push({ day: DAY_ORDER[date.getDay()], ...event, isToday: offset === 0 });
    }
  }
  return highlights;
};

const Hero = () => {
  const [featuredEvent, setFeaturedEvent] = useState(null);

  const fetchFeaturedEvent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/featured-event`);
      if (response.data) {
        setFeaturedEvent(response.data);
      }
    } catch (err) {
      // Expected when no featured event is set - not an error
      if (process.env.NODE_ENV === 'development' && err.response?.status !== 404) {
        console.error('Failed to fetch featured event:', err.message);
      }
    }
  }, []);

  useEffect(() => {
    fetchFeaturedEvent();
  }, [fetchFeaturedEvent]);

  const scrollToMenu = () => {
    const element = document.getElementById('menu');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const weekHighlights = getWeekHighlights();

  return (
    <section id="hero" className="hero-section">
      {/* Blended dual-cuisine photo background */}
      <div className="hero-background">
        <div className="hero-img hero-img-american" />
        <div className="hero-img hero-img-indian" />
        <div className="hero-overlay" />
        <div className="hero-bokeh" />
        <div className="hero-watermark" aria-hidden="true" />
      </div>

      {/* Main content */}
      <div className="hero-content">
        {/* Left side - Branding */}
        <div className="hero-branding">
          <div className="hero-badge">
            <span>Albany's Premier Bar & Grill</span>
          </div>

          <h1 className="hero-title">
            <span className="title-line">Elevated American</span>
            <span className="title-line">Classics.</span>
            <span className="title-line accent">Authentic Indian</span>
            <span className="title-line accent">Fusion.</span>
          </h1>

          <p className="hero-subtitle">
            Premium tequila. Crafted cocktails. Polished atmosphere.
          </p>

          <div className="hero-buttons">
            <button onClick={scrollToMenu} className="btn-hero-primary">
              Explore Menu
              <ChevronDown size={20} />
            </button>
            <a href={EVENTBRITE_URL} target="_blank" rel="noopener noreferrer" className="btn-hero-secondary" data-testid="reserve-seat-hero" onClick={() => trackEvent('reserve_seat_click', { link_location: 'hero' })}>
              Reserve a Seat
            </a>
            <a href={ORDER_URL} target="_blank" rel="noopener noreferrer" className="btn-hero-primary" data-testid="order-now-hero" onClick={() => trackEvent('order_now_click', { link_location: 'hero' })}>
              <ShoppingBag size={18} />
              Order Now
            </a>
          </div>

          {/* Quick Info */}
          <div className="hero-quick-info">
            <div className="quick-info-item">
              <MapPin size={16} />
              <span>859 Belmont Ave SW #120, Albany, OR</span>
            </div>
            <div className="quick-info-item">
              <Phone size={16} />
              <span>(458) 253-4058</span>
            </div>
          </div>
        </div>

        {/* Right side - Featured Event / Weekly Specials Card */}
        <div className="hero-featured-card">
          <div className="featured-card-header">
            <CalendarDays size={28} />
            <div className="header-text">
              <span className="header-title">What's Happening</span>
              <span className="header-subtitle">This Week</span>
            </div>
          </div>
          
          <div className="featured-card-content">
            {/* Featured/Upcoming Event */}
            {featuredEvent && (
              <div className="featured-event-highlight main-event">
                <span className="event-tag">UPCOMING EVENT</span>
                <h3 className="event-title">{featuredEvent.title}</h3>
                {featuredEvent.subtitle && (
                  <p className="event-subtitle">{featuredEvent.subtitle}</p>
                )}
                <div className="event-details">
                  <span className="event-date">
                    <Calendar size={14} />
                    {featuredEvent.date}
                  </span>
                  {featuredEvent.time && (
                    <span className="event-time">
                      <Clock size={14} />
                      {featuredEvent.time}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Today + next two upcoming events (Pacific Time, auto-rotating) */}
            <div className="happening-list" data-testid="happening-list">
              {weekHighlights.map((item) => {
                const ItemIcon = item.Icon;
                return (
                  <div
                    key={item.day}
                    className={`happening-item ${item.isToday ? 'today' : ''}`}
                    data-testid={`happening-${item.day.toLowerCase()}`}
                  >
                    <span className="happening-icon">
                      <ItemIcon size={20} />
                    </span>
                    <div className="happening-info">
                      <span className="happening-day">
                        {item.day}
                        {item.isToday && <span className="today-badge">Today</span>}
                      </span>
                      <span className="happening-name">{item.name}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="featured-card-footer">
            <a href="#events" className="view-all-link">
              View All Events
              <ChevronDown size={16} className="rotate-270" />
            </a>
            <a href={EVENTBRITE_URL} target="_blank" rel="noopener noreferrer" className="eventbrite-card-link" data-testid="eventbrite-hero-link" onClick={() => trackEvent('eventbrite_click', { link_location: 'hero' })}>
              <Ticket size={14} />
              Upcoming Events on Eventbrite
            </a>
          </div>
        </div>
      </div>

      {/* Decorative accent line */}
      <div className="hero-accent-line" />
    </section>
  );
};

export default Hero;
